// The client's experience strip — one component, two homes (2026-09-05;
// rebuilt to the approved mockup 2026-09-06).
//
// CONSUMER: internal-ui
//
// It sits at the top of the form builder AND inside the appointment
// type editor, mirrored: step 1 is the thing you're editing, step 2 is
// the slot for the other half. Learn it once. The facts on it (pages,
// questions, minutes, lengths, kinds) are computed from the record, never
// typed, and the sentence in the head states the ORDER the client meets
// the two halves in for THIS door — a form link runs questions then the
// calendar, a meeting link holds the time then asks.
//
// Shape (mockup .strip): a band with a tinted label panel on the left,
// then step 1, an arrow, and step 2 — filled, or a dashed slot whose
// title IS the call to action ("+ Add a form"). Under the band, a foot
// line: the connection read the other way (or what happens today) on
// the left, "Preview the client's experience →" on the right.

import type { ReactNode } from "react";
import { ArrowRight } from "lucide-react";

export interface StripStepFilled {
  kind: "filled";
  label: string;
  title: string;
  meta: string;
  actions?: ReactNode;
}

export interface StripStepEmpty {
  kind: "empty";
  label: string;
  cta: string;
  hint: string;
  onAdd: () => void;
  disabled?: boolean;
  disabledReason?: string;
}

export function ExperienceStrip({
  lede,
  first,
  second,
  note,
  preview,
}: {
  /** One sentence under the eyebrow: what someone goes through. */
  lede: string;
  first: { label: string; title: string; meta: string };
  second: StripStepFilled | StripStepEmpty;
  /** The foot's left half: the connection read the other way, or what
   *  happens today when nothing is connected. */
  note?: ReactNode;
  preview?: { label: string; onClick: () => void };
}) {
  return (
    <section className="cx-strip-wrap" aria-label="The client's experience">
      <div className="cx-strip">
        <div className="cx-strip-head">
          <p className="cx-eyebrow">The client&rsquo;s experience</p>
          <span className="cx-quiet">{lede}</span>
        </div>
        <div className="cx-step cx-step--filled">
          <span className="cx-step-num" aria-hidden="true">
            1
          </span>
          <div className="cx-step-body">
            <span className="cx-step-label">{first.label}</span>
            <div className="cx-step-title">{first.title}</div>
            <div className="cx-step-meta">{first.meta}</div>
          </div>
        </div>
        <span className="cx-arrow" aria-hidden="true">
          <ArrowRight size={18} strokeWidth={1.75} />
        </span>
        {second.kind === "filled" ? (
          <div className="cx-step cx-step--filled">
            <span className="cx-step-num" aria-hidden="true">
              2
            </span>
            <div className="cx-step-body">
              <span className="cx-step-label">{second.label}</span>
              <div className="cx-step-title">{second.title}</div>
              <div className="cx-step-meta">{second.meta}</div>
            </div>
            {second.actions && <div className="cx-step-actions">{second.actions}</div>}
          </div>
        ) : (
          <div className="cx-step cx-step--empty">
            <span className="cx-step-num" aria-hidden="true">
              2
            </span>
            <div className="cx-step-body">
              <span className="cx-step-label">{second.label}</span>
              <button
                type="button"
                className="cx-step-cta"
                onClick={second.onAdd}
                disabled={second.disabled}
              >
                + {second.cta}
              </button>
              <div className="cx-step-meta">
                {second.disabled && second.disabledReason ? second.disabledReason : second.hint}
              </div>
            </div>
          </div>
        )}
      </div>
      {(note || preview) && (
        <div className="cx-strip-foot">
          <span className="cx-strip-note">{note}</span>
          {preview && (
            <button type="button" className="cx-btn-link" onClick={preview.onClick}>
              {preview.label} →
            </button>
          )}
        </div>
      )}
    </section>
  );
}

/** Plain-language company names, the five a client can book with. */
export const CLIENT_COMPANY_LABELS: Record<string, string> = {
  mortgage: "Mortgage",
  insurance: "Insurance",
  planning: "Planning",
  realty: "Realty",
  financial: "Financial",
};

export const CLIENT_COMPANY_SLUGS = [
  "mortgage",
  "insurance",
  "planning",
  "realty",
  "financial",
] as const;
